import { DotLegalDeleteDialog, DotLegalMultiSelect } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useSelectableBusinessAreaByUserAccessQuery } from "../../common/hooks/useSelectableItemQueries.ts";
import { AccessControlEntity, useAccessResponsibilityHelper } from "./useAccessResponsibilityHelper.hooks.ts";
import { Trans } from "react-i18next";

export interface BusinessAreaAccessFieldProps<T extends AccessControlEntity> {
    entity: T | undefined;
    isLoading: boolean;
    onChange: (changeFunc: (entity: T) => void) => Promise<void>;
    onConfirmNavigate: () => void;
    readOnly?: boolean;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
    toolTipText?: string;
    warningText?: string;
    disableAccessWarning?: boolean;
}

function BusinessAreaAccessField<T extends AccessControlEntity>(props: BusinessAreaAccessFieldProps<T>) {
    const { translateString } = useTranslation();
    let businessAreaQuery = useSelectableBusinessAreaByUserAccessQuery(!props.isPreview);

    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useAccessResponsibilityHelper<T>(
        props.onChange,
        props.onConfirmNavigate,
        props.disableAccessWarning
    );

    return (
        <>
            <DotLegalMultiSelect
                options={businessAreaQuery.data}
                isLoading={(businessAreaQuery.isLoading || props.isLoading) && !props.readOnly}
                chosenOptions={props.entity?.accessBusinessAreas ?? []}
                onChange={
                    props.readOnly
                        ? () => {}
                        : (businessAreas) => {
                              const visibleBusinessAreaIds = businessAreaQuery.data?.map((ba) => ba.id) ?? [];
                              const hiddenBusinessAreas = props.entity?.accessBusinessAreas.filter((id) => !visibleBusinessAreaIds.includes(id)) ?? [];
                              const allBusinessAreas = [...hiddenBusinessAreas, ...businessAreas];

                              let newEntity = { ...props.entity!, accessBusinessAreas: allBusinessAreas };
                              onFieldChange(newEntity, props.entity!);
                          }
                }
                label={translateString("businessAreaAccess")}
                noOptionsLabel={translateString("noOptions")}
                disabled={props.disabled}
                toolTipText={props.toolTipText}
                warningText={props.isPreview || props.disableAccessWarning ? undefined : props.warningText}
                errorText={props.errorText}
                disableClearable
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey="confirmSystemAccessRemovalBody"
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}
        </>
    );
}

export default BusinessAreaAccessField;
